import React, { useEffect, useMemo,useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField 
} from "@mui/material";
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { showErrorSnackbar } from 'cui/components/snackbar/Snackbar.slice';
import { useInsightApi } from "hooks/use-insight-api";
import { useAppDispatch, useAppSelector } from "store/app/hooks";
import { 
  setClientDropDownData,
  setSelectedClient, 
  setReportClientDropDownData,
  setSelectedReportClient,
  setSelectedReportEngagement,
} from "./breadcrumb.slice";
import { RootState } from "store/app/store";
import { ErrorCodesUI } from "constants/ErrorCodes";
import ReportsClientBreadcrumbPopUp from 'cui/components/popup/ReportsClientBreadcrumbPopUp';
import ReportsClientPopUp from 'cui/components/popup/ReportsClientPopUp';

export type DropDownProps = {
  data: Option[];
  currentOption: string;
  isClientDropDownNeeded: boolean;
  isClientDropDown: boolean;
  isReportClientDropDownNeeded: boolean;
  isReportClientDropDown: boolean;
  isReportEngagementDropDown: boolean;
}

export type Option = {
  id: string;
  value: string;
}

const containsText = (text: string, searchText: string) => {
  return text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;};

const Dropdown = (props: DropDownProps) => {
  const dispatch = useAppDispatch();
  const { checkIfClientAdmin, getClientsForAdmin, getClientsForUserReports, getEngagementsForUserReports } = useInsightApi();
  const [allOptions, setAllOptions] = useState<Option[]>([]);
  const [selectedOption, setSelectedOption] = useState<Option>({} as Option);
  const [searchText, setSearchText] = useState<string>("");
  const currentApplication = useAppSelector((state: RootState) => state.applications.selectedApplication);
  const location = useLocation();
  const navigate = useNavigate();
  const [showEngagementPopup, setShowEngagementPopup] = useState(false);
  const [popupClient, setPopupClient] = useState<Option | null>(null);
  const [popupClientsData, setPopupClientsData] = useState<Option[]>([]);
  const [pendingClientId, setPendingClientId] = useState<string | null>(null);
  const [showApplicationClientPopup, setShowApplicationClientPopup] = useState(false);
  const [popupApplicationId, setPopupApplicationId] = useState<string>('');
  const [popupApplicationName, setPopupApplicationName] = useState<string>('');

  useEffect(() => {
    if (props.data) {
      setAllOptions(props.data);
    }
    if(props.currentOption)
    {
      setSelectedOption(props.data.find((x) => x.id === props.currentOption)?? {} as Option);
    }
  }, [props]);

  const displayedOptions = useMemo(() => {
      return allOptions.filter((option) => {
        return containsText(option.value, searchText);
      }
  )}, [allOptions, searchText]);

  const handleApplicationOptionChange = async (e: any) => {
    const currentSelectedOption = allOptions.find((option) => option.id === e.target.value);
    if (currentSelectedOption) {
      if(props.isClientDropDownNeeded === true){
        await checkIfApplicationClientAdmin(currentSelectedOption.id).then(async result => {
          if (result) {
            var initialClient = await populateApplicationClientData(currentSelectedOption.id);
            setSelectedOption(currentSelectedOption);
            navigate(getNavigationPathWithTwoParams(currentSelectedOption.id, initialClient!.toString()));
          }
          else {
            showErrorSnackbar(dispatch, `${ErrorCodesUI.NotClientAdmin} - You are not a client admin for this application.`);
          }
        }).catch(error => {
          console.error(`${ErrorCodesUI.NotClientAdmin} - Error checking admin status:`, error);
          showErrorSnackbar(dispatch, `${ErrorCodesUI.NotClientAdmin} - You are not a client admin for this application.`);
        });
      }
      else if(props.isReportClientDropDownNeeded === true){
        setPopupApplicationId(currentSelectedOption.id);
        setPopupApplicationName(currentSelectedOption.value);
        setShowApplicationClientPopup(true);
      }
      else{
        setSelectedOption(currentSelectedOption);
        navigate(getNavigationPath(currentSelectedOption.id));
      }
    }
  };

  const handleClientOptionChange = async (e: any) => {
    const currentSelectedOption = allOptions.find((option) => option.id === e.target.value);
    if (currentSelectedOption) {
      setSelectedOption(currentSelectedOption);
			dispatch(setSelectedClient(currentSelectedOption.id));
      navigate(getNavigationPathWithTwoParams(currentApplication, currentSelectedOption.id));
    }
  };

  const handleReportClientOptionChange = async (e: any) => {
    const currentSelectedOption = allOptions.find((option) => option.id === e.target.value);
    if (currentSelectedOption) {
      setSelectedOption(currentSelectedOption);
      setPopupClient(currentSelectedOption);
      setPopupClientsData(allOptions);
      setShowEngagementPopup(true);
    }
  };

  const handleReportEngagementOptionChange = async (e: any) => {
    const currentSelectedOption = allOptions.find((option) => option.id === e.target.value);
    if (currentSelectedOption) {
      setSelectedOption(currentSelectedOption);
      dispatch(setSelectedReportEngagement(currentSelectedOption.id));
      navigate(getNavigationPath(currentSelectedOption.id));
    }
  };

  const getNavigationPath = (currentOptionId: string) => {
    const lastSegment = location.pathname.substring(location.pathname.lastIndexOf('/') + 1);
    return location.pathname.replace(lastSegment, currentOptionId);
  };

  const getNavigationPathWithTwoParams = (param1: string, param2: string) => {
    const secondParameter = location.pathname.substring(location.pathname.lastIndexOf('/') + 1);
    const pathWithoutSecondParam = location.pathname.substring(0, location.pathname.lastIndexOf('/'));
    const firstParameter = pathWithoutSecondParam.substring(pathWithoutSecondParam.lastIndexOf('/') + 1);
    return location.pathname.replace(firstParameter, param1).replace(secondParameter, param2);
  };

  const checkIfApplicationClientAdmin = async (currentOptionId: string) => {
    try {
			const response: boolean = await checkIfClientAdmin(currentOptionId!);
			if (response) {
				return response;
			}
		} catch (e) {
			  return false;
		}
  };

  const populateApplicationClientData = async (currentOptionId: string) => {
    const data = await getClientsForAdmin(currentOptionId);
		if(data){
			dispatch(setClientDropDownData(data.map((r) => {
				return { id: r.key, value: r.value } as Option;
			})));
			dispatch(setSelectedClient(data[0].key ?? ''));
      setSelectedOption({ id: data[0].key, value: data[0].value } as Option);
      return data[0].key;
		}
		else {
      showErrorSnackbar(dispatch, `${ErrorCodesUI.NotClientAdmin} - You are not a client admin for this application.`);
		}
  };

  const populateUserReportClientData = async (currentOptionId: string) => {
    try{
      const data = await getClientsForUserReports(currentOptionId);
      if(data){
        dispatch(setReportClientDropDownData(data.map((r) => {
          return { id: r.key, value: r.value } as Option;
        })));
        dispatch(setSelectedReportClient(data[0].key ?? ''));
        setSelectedOption({ id: data[0].key, value: data[0].value } as Option);
        return data[0].key;
      }
      else {
        showErrorSnackbar(dispatch, `${ErrorCodesUI.NoClientsForApplication} - You do not have any clients for this application.`);
      }
    }
    catch(e){
      showErrorSnackbar(dispatch, `${ErrorCodesUI.NoClientsForApplication} - You do not have any clients for this application.`);
    }
  };

  const selectStyles = {
    "#search-select": { padding: '0', paddingRight: '15px', width: 'auto' },
    "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: 0,
    },
    "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      border: 0,
    },
    ".MuiOutlinedInput-notchedOutline": { border: 0 },
    boxShadow: "none",
    fontSize: '13px',
  };

  return (
    <>
      <Box>
        <Select
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 250,
                overflowY: "auto", 
                padding: '10px',
              },
            },
            autoFocus: false,
          }}
          labelId="search-select-label"
          id="search-select"
          value={selectedOption?.id ?? ''}
          onChange={props.isClientDropDown ? handleClientOptionChange :  
            props.isReportClientDropDown ? handleReportClientOptionChange : 
            props.isReportEngagementDropDown ? handleReportEngagementOptionChange :
            handleApplicationOptionChange}
          onClose={() => setSearchText("")}
          renderValue={() => selectedOption.value }
          native={false}
          displayEmpty
          sx={selectStyles}
        >
          <ListSubheader>
            <TextField
              size="small"
              autoFocus
              placeholder="Search..."
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Escape") {
                  e.stopPropagation();
                }
              }}
            />
          </ListSubheader>
          {displayedOptions.map((option) => (
            <MenuItem sx={{ fontSize: '13px' }} key={option.id} value={option.id}>
              {option.value}
            </MenuItem>
          ))}
        </Select>
      </Box>
      {showEngagementPopup && popupClient && (
        <ReportsClientBreadcrumbPopUp
          client={{ key: popupClient.id, value: popupClient.value }}
          applicationId={currentApplication}
          applicationName={popupClient.value}
          isVisible={showEngagementPopup}
          onClose={() => setShowEngagementPopup(false)}
          clientsData={popupClientsData.map(c => ({ key: c.id, value: c.value }))}
        />
      )}
      {showApplicationClientPopup && (
        <ReportsClientPopUp
          applicationId={popupApplicationId}
          applicationName={popupApplicationName}
          isVisible={showApplicationClientPopup}
          onClose={() => setShowApplicationClientPopup(false)}
        />
      )}
    </>
  );
}

export default React.memo(Dropdown);
